<script lang="ts" context="module">
    export enum TutorialState {
        INPUT = "input",
        LOADING = "loading",
        PAGE = "page",
        ERROR = "error",
    }
</script>

<script lang="ts">
    import { slide, fade, fly } from "svelte/transition";

    import IntroImg1 from "$/assets/images/intro-01.png";

    import Highlighter from "$/Highlighter.svelte";
    import { CopilotService } from "$/services/CopilotService";
    import { clickAction } from "$/utils/clickAction";
    import { ActiveTopicStore, ActiveTopic } from "$/stores/extension/ActiveTopicStore";

    import { IntroModalStore } from "$/components/modals/IntroModal/IntroModalStore";
    import { onMount } from "svelte";
    import {PAGE_WRAPPER_ID} from "$/utils/Utils";

    let state: TutorialState = TutorialState.INPUT;
    let researchTopic: string = "";

    let snippets = [];

    let snippet;
    let location;

    let selectionIsEmpty = false;
    document.addEventListener("selectionchange", () => {
        selectionIsEmpty = window.getSelection().toString().length === 0;
    });
    let highlighterIsOpen = false;

    $: showTutorialImage = selectionIsEmpty && !highlighterIsOpen;

    let suggestions = {
        default: [
            "How to be more productive",
            "How to be more creative",
            "Knowledge Management Strategies",
        ],
        marketer: [
            "Content Marketing Market Size",
            "Content Marketing Trends 2024",
            "Content Strategies",
            "SEO Hacks you should know",
        ],
        founder: [
            "PIRATE Metrics",
            "Startup Growth Strategies",
            "Best Go-to-Market Framework",
            "Startup Exit Strategies",
        ],
        consultant: [
            "How to be a better Consultant",
            "Consulting Trends 2024",
            "Business Consulting Strategies",
            "Consulting Business Plan",
        ],
        investor: [
            "Startup Investments Trends 2024",
            "Investment Strategies",
            "Investment Exit Strategies",
        ],
        lawyer: [
            "Legal Market Size",
            "Legal Trends 2024",
            "Legal Strategies",
            "Legal Exit Strategies",
        ],
        researcher: [
            "Research Market Size",
            "Research Trends 2024",
            "Research Strategies",
            "Research Exit Strategies",
        ],
        student: ["Strategies to learn faster", "How to take better notes"],
    };
    let userSuggestions = [];

    const createCopilotQuery = async () => {
        state = TutorialState.LOADING;

        let query = await CopilotService.createQuery(
            {
                query: researchTopic,
                scheduled: true,
            },
            10,
        );

        snippets = query.snippets ?? [];

        // TODO: Handle Errors
        if (snippets.length === 0) {
            state = TutorialState.ERROR;
            return;
        }

        snippet = snippets[0];
        location = new URL(snippet.url);
        document.title = snippet.nameSuggestion;

        $IntroModalStore.suggestions = snippets;

        state = TutorialState.PAGE;
    };

    const onInputKeyPress = async (event) => {
        if (event.key === "Enter") {
            await createCopilotQuery();
        }
    };

    const clickSuggestion = (suggestion) => {
        researchTopic = suggestion;
        createCopilotQuery();
    };

    onMount(async () => {
        // init store
        await IntroModalStore.init();

        // get user-specific suggestions
        $IntroModalStore.userRoles?.forEach((role) => {
            userSuggestions = [
                ...userSuggestions,
                ...suggestions[role.toLowerCase()],
            ];
        });

        // add default suggestions
        userSuggestions = [...userSuggestions, ...suggestions.default];
    });
</script>

<div id={PAGE_WRAPPER_ID} class="flex h-screen flex-col">
    <div class="z-0 flex flex-1 flex-col">
        <!-- Show Loading -->
        {#if state === TutorialState.LOADING}
            <div
                class="m-auto flex max-w-md flex-col overflow-clip rounded-xl border border-gray-300"
                in:fly
            >
                <span
                    class="select-none border-b border-gray-300 bg-gray-100 px-4 py-1 text-sm text-gray-400"
                >
                    Searching the web
                </span>
                <img class="w-full" alt="intro" src="./images/intro-02.gif" />
            </div>

            <!-- Show Search Bar -->
        {:else if state === TutorialState.INPUT}
            <div class="m-auto flex w-full max-w-md flex-col gap-4 p-4">
                <h1 class="text-center text-2xl">
                    What shall we <span class="bg-yellow-300 px-1 font-bold"
                        >research</span
                    > today? 👀
                </h1>

                <div
                    class="flex w-full max-w-md flex-row items-center gap-2 rounded-full border border-gray-800 py-2 pl-4 pr-2 text-xl"
                >
                    <svg
                        class="h-6 w-6"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                        />
                    </svg>
                    <input
                        class="w-full flex-1 outline-none"
                        name="search"
                        type="text"
                        placeholder="Content Marketing Market Size"
                        bind:value={researchTopic}
                        on:keypress={onInputKeyPress}
                    />

                    {#if researchTopic.length > 0}
                        <div
                            class="copilot-input-search-icon"
                            use:clickAction={createCopilotQuery}
                        >
                            <svg
                                class="h-5 w-5"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                                />
                            </svg>
                        </div>
                    {/if}
                </div>

                {#if researchTopic.length > 0}
                    <span
                        in:fade|local
                        class="pr-4 text-right text-sm text-gray-500"
                        >Press <span
                            class="rounded-sm bg-gray-200 px-1 py-0.5 text-gray-500"
                            >⏎ Enter</span
                        > to start</span
                    >
                {:else}
                    <div
                        class="my-2 flex w-full max-w-md flex-wrap items-center justify-center gap-2"
                        transition:slide|local
                    >
                        {#each userSuggestions as suggestion}
                            <span
                                on:click={() => clickSuggestion(suggestion)}
                                on:keyup|preventDefault|stopPropagation
                                on:keydown|preventDefault|stopPropagation
                                on:keypress|preventDefault|stopPropagation
                                transition:slide|local
                                class="min-w-max cursor-pointer rounded-full border border-blue-400 bg-blue-200 px-4 py-1 text-sm text-blue-500 transition-all hover:scale-105 hover:bg-blue-300 hover:text-blue-600 hover:shadow-md"
                            >
                                {suggestion}
                            </span>
                        {/each}
                    </div>
                {/if}
            </div>
        {:else if state === TutorialState.PAGE}
            <div class="flex flex-1 flex-col items-center justify-center">
                <div class="flex max-w-2xl flex-col gap-4 p-8">
                    <h2 class="select-none text-4xl font-bold">
                        {snippet.nameSuggestion}
                    </h2>

                    <p
                        class="max-h-15vh select-none overflow-hidden text-xl blur-sm"
                    >
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                        Duis hendrerit molestie massa, vitae sodales mauris
                        aliquam in. Fusce auctor lacinia sapien, sed interdum
                        ipsum hendrerit at. Nam eu volutpat elit. Suspendisse
                        potenti. Sed commodo tincidunt eleifend. Nullam tempus
                        vel nisi eget vehicula. Vestibulum porta sem in nisi
                        tempus imperdiet. In hac habitasse platea dictumst.
                        Morbi eget mauris vitae lectus vulputate sodales. Cras
                        ut dui at dui egestas congue. Ut viverra neque arcu,
                        dignissim posuere libero volutpat a. Nulla sit amet
                        condimentum ipsum. Ut aliquet mi vel ante eleifend
                        bibendum. Phasellus tempus non felis a dictum. Aliquam
                        posuere, magna lobortis faucibus congue, mi arcu
                        tristique elit, eu facilisis eros mi dictum felis. Duis
                        condimentum pulvinar diam eu facilisis. Aenean lacinia
                        pharetra justo, et volutpat leo interdum vel. Vestibulum
                        auctor volutpat nisi at auctor. In hac habitasse platea
                        dictumst. Ut ut diam velit.
                    </p>

                    <div class="relative">
                        <p
                            class="white-shadow text-xl selection:bg-yellow-300 selection:text-black"
                            on:contextmenu|preventDefault|stopPropagation
                        >
                            {@html snippet.content}
                        </p>
                        <br />
                        {#if showTutorialImage}
                            <img
                                class="absolute -bottom-44 left-16 z-20 h-40"
                                transition:fade|local
                                src={IntroImg1}
                                alt="Highlight some text"
                            />
                        {/if}
                    </div>

                    <p
                        class="max-h-35vh select-none overflow-hidden text-xl blur-sm"
                    >
                        Suspendisse a purus ultricies, posuere tellus at,
                        maximus tellus. Sed elementum mollis neque, pellentesque
                        molestie sapien laoreet ultricies. Etiam lorem justo,
                        euismod in porttitor eget, viverra lacinia sapien.
                        Maecenas pellentesque at ligula vitae sollicitudin.
                        Donec viverra velit ante, eu viverra erat sagittis at.
                        Aliquam condimentum felis quis arcu pretium vestibulum.
                        Suspendisse consectetur venenatis risus, id ornare justo
                        facilisis non. Mauris tempor eget massa et auctor.
                        Quisque convallis molestie bibendum. Nam at lectus
                        porta, dignissim dui ut, dignissim felis. Praesent ut
                        nulla libero. Aliquam vitae porttitor erat. Integer sit
                        amet nisi in dolor volutpat ultricies venenatis sed
                        enim. Duis auctor ut elit lacinia semper. Proin suscipit
                        urna eu bibendum tempor. Phasellus vitae lacus et quam
                        tincidunt placerat. In rhoncus eros leo, sit amet mollis
                        sem commodo at. Cras ex purus, hendrerit nec dignissim
                        eu, ultricies ut augue. Morbi auctor euismod lacus,
                        tincidunt consequat massa tempus ac.
                    </p>
                </div>
            </div>

            <Highlighter
                {location}
                showSnippetCreated={false}
                on:close={() => (highlighterIsOpen = false)}
                on:open={() => (highlighterIsOpen = true)}
                on:create={() => {
                    window.location.href = `/${$ActiveTopic.id}/#intro`;
                }}
            ></Highlighter>
        {/if}
    </div>
</div>

<style>
    @import "../../main.css";

    /* purgecss start ignore */

    /** HACK: Fix Editor Styles */
    :global(.modal-wrapper__line) {
        @apply mx-0 my-2 !important;
    }
    /* purgecss end ignore */

    .max-h-35vh {
        @apply max-h-[35vh];
    }

    .max-h-15vh {
        @apply max-h-[15vh];
    }

    .loading {
        @apply m-auto w-full max-w-xs hue-rotate-[290deg];
    }

    .white-shadow {
        @apply relative z-10 shadow-[0px_0px_50px_50px_rgba(255,255,255,1)];
    }

    .white-backdrop-shadow {
        filter: drop-shadow(0px 10px 10px white);
    }

    .copilot-input-search-icon {
        @apply flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-pink-500 text-white transition-all hover:scale-110;
    }
</style>
